/**
 * "Matches right now" — the resolved lists for the draft rule, live. Shows a
 * capped preview with a name filter and show-all past the cap; a resolve
 * failure degrades to a muted note and never blocks Save.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { Search } from 'lucide-react';

import type { ListGroupResolve } from '../../../list-groups.types';
import { MatchRow } from './MatchRow';
import { MATCHES_ROW_CAP, useMatchesDisplay } from './useMatchesDisplay';

const GRID_CLASS =
  'grid grid-cols-[repeat(auto-fill,minmax(230px,1fr))] gap-1.5';

interface Props {
  resolve: ListGroupResolve | undefined;
  loading: boolean;
  failed: boolean;
  /** Tracker stats warming — the resolve is deliberately paused. */
  warming?: boolean;
  /** Leaf conditions in the draft rule — chips are noise below 2. */
  conditionCount: number;
}

export const MatchesRightNow: FC<Props> = ({
  resolve,
  loading,
  failed,
  warming = false,
  conditionCount,
}) => {
  const display = useMatchesDisplay(resolve);
  const showWhy = conditionCount > 1;

  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-center justify-between border-b border-border/60 pb-2">
        <h3 className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          <Search className="h-3.5 w-3.5" /> Matches right now
          {resolve && resolve.count > 0 ? (
            <span className="font-normal normal-case tracking-normal">
              · {resolve.count} of {resolve.totalLists} lists
            </span>
          ) : null}
        </h3>
        <span className="text-xs text-muted-foreground">
          biggest first — each container decides its own consumption order
        </span>
      </div>
      {warming ? (
        <p className="text-sm text-muted-foreground">
          Tracker stats are warming up (see the progress above) — the live
          match preview appears when they&apos;re ready. Saving works now.
        </p>
      ) : loading ? (
        <div className={GRID_CLASS}>
          {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
            <div key={i} className="h-9 animate-pulse rounded-md bg-muted/60" />
          ))}
        </div>
      ) : failed ? (
        <p className="text-sm text-muted-foreground">preview unavailable</p>
      ) : !resolve || resolve.count === 0 ? (
        <p className="text-sm text-muted-foreground">
          {resolve
            ? `Nothing matches yet — 0 of the ${resolve.totalLists} lists in this country satisfy the rule. `
            : 'Nothing matches this rule right now. '}
          Matching is live: a list that gains the tag or a matching name joins on
          its own.
        </p>
      ) : (
        <>
          {resolve.count > MATCHES_ROW_CAP ? (
            <label className="flex items-center gap-2 rounded-md border border-input px-2.5 py-1.5 focus-within:border-primary/50">
              <Search className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <input
                value={display.query}
                onChange={(e) => display.setQuery(e.target.value)}
                placeholder={`Filter ${resolve.count} lists by name…`}
                className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
              />
            </label>
          ) : null}
          {display.filteredCount === 0 ? (
            <p className="text-sm text-muted-foreground">
              No matching list name contains “{display.query.trim()}”.
            </p>
          ) : (
            <>
              <ul
                className={`${GRID_CLASS} ${display.scrolls ? 'max-h-96 overflow-y-auto pr-1' : ''}`}
              >
                {display.visible.map((row) => (
                  <MatchRow
                    key={row.wiserId}
                    row={row}
                    showWhy={showWhy}
                    showLeads={display.hasLeads}
                  />
                ))}
              </ul>
              {display.hiddenCount > 0 ? (
                <button
                  type="button"
                  onClick={() => display.setExpanded(true)}
                  className="self-start text-xs font-medium text-primary hover:underline"
                >
                  Show all {display.filteredCount} lists
                </button>
              ) : display.expanded && !display.query.trim() ? (
                <button
                  type="button"
                  onClick={() => display.setExpanded(false)}
                  className="self-start text-xs font-medium text-primary hover:underline"
                >
                  Show less
                </button>
              ) : null}
            </>
          )}
        </>
      )}
    </section>
  );
};
